// Tekstiltrykk (/service-details-club/:id), i startsidens stil som kundeordre (OrderDetails.js),
// vinnsel 30.10: kunden øverst (lenke til kundekortet), SMS-er til kunden, «Skriv ut etikett»,
// «Skriv ut arbeidsseddel» og «Send SMS», så trykket (skjemaet), arbeid og produkter, pristilbud og
// meldinger med kunden (kundelenken /kundetrykk/…).
// Arbeid og produkter er én liste med prislinjer (Service.prislinjer, src/lib/trykkLinjer.js) i
// pristilbudets form, med rabatt i % eller kr. De legges til og endres i en dialog (trykk/TrykkLinjer.jsx)
// og lagres med en gang. Linjene og betalingen (tøy/trykk) er grunnlaget for arbeidsseddelen
// (src/trykk/arbeidsseddel.js), og linjene for pristilbudet, som sendes herfra (tilbud/TrykkTilbud.jsx);
// et tilbud kunden ikke har svart på, får nye linjer når de lagres.
// Bare tekstiltrykk; sykkel, ski og skøyteslip har egne sider og registreres i butikkmodus.
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import SmsTilKunden from './components/SmsTilKunden';
import OrdreMeldinger from './components/OrdreMeldinger';
import TrykkTilbud from './tilbud/TrykkTilbud';
import { arbeidsseddelHtml } from './trykk/arbeidsseddel';
import { Tilbake, Sidetopp, Tekstfelt, Valgfelt, Valgknapper, feltKlasse, knappPrimar, knappSekundar } from './ny-kundesamhandling/Skjema';
import Ikon from './ny-kundesamhandling/Ikon';
import KundeBanner from './components/KundeBanner';
import KlubbKontaktBanner, { KlubbMerke, kontaktSomMottaker } from './components/KlubbKontaktBanner';
import { getArbeidsliste } from './lib/tjenestekatalogApi';
import TrykkLinjer from './trykk/TrykkLinjer';
import { linjerForTrykk, linjeNokkel } from './trykk/linjer';
import formatDateTime from './hjelpere/formatDateTime.js';
import { escapeHtml } from './utils/format';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STATUSER = [
  { verdi: 'Aktiv', tittel: 'Aktiv' },
  { verdi: 'Avsluttet', tittel: 'Avsluttet' },
];

// Skjemafeltene fra trykket (ferdigDato lagres som ISO med klokkeslettet slik det ble skrevet).
function fraTrykk(s) {
  const iso = s.ferdigDato ? String(s.ferdigDato) : '';
  return {
    Varemerke: s.Varemerke || '',
    Produkt: s.Produkt || '',
    Størrelse: s.Størrelse || '',
    Farge: s.Farge || '',
    beskrivelse: s.Beskrivelse || '',
    utførtArbeid: s.utførtArbeid || '',
    ansatt: s.ansatt || '',
    status: s.status || 'Aktiv',
    ferdigDato: iso ? iso.split('T')[0] : '',
    ferdigTid: iso.includes('T') ? iso.split('T')[1].slice(0, 5) : '',
    toyBetalt: !!s.toyBetalt,
    trykkBetalt: !!s.trykkBetalt,
  };
}

function ServiceDetailsClub() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Fra «Registrer trykk»: ble SMS-en med kundelenken sendt? (CreateService.js)
  const smsLenke = useLocation().state?.smsLenke || null;

  const [trykk, setTrykk] = useState(null);
  const [ikkeFunnet, setIkkeFunnet] = useState(false);
  const [kunde, setKunde] = useState(null);
  const [ansatte, setAnsatte] = useState([]);
  const [arbeidsliste, setArbeidsliste] = useState([]);
  const [form, setForm] = useState(null);
  const [lagrer, setLagrer] = useState(false);
  const [melding, setMelding] = useState('');
  const [feil, setFeil] = useState('');
  const [linjer, setLinjer] = useState([]);
  const [lagrerLinjer, setLagrerLinjer] = useState(false);
  const [linjeMelding, setLinjeMelding] = useState('');
  const [linjeFeil, setLinjeFeil] = useState('');

  useEffect(() => {
    let avbrutt = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/services/${id}`);
        if (!r.ok) { if (!avbrutt) setIkkeFunnet(true); return; }
        const s = await r.json();
        if (avbrutt) return;
        setTrykk(s);
        setForm(fraTrykk(s));
        setLinjer(linjerForTrykk(s));
        if (s.kundeid != null) {
          const kr2 = await fetch(`${API_BASE_URL}/customers?customerNumber=${encodeURIComponent(s.kundeid)}`);
          const liste = kr2.ok ? await kr2.json() : [];
          if (!avbrutt) setKunde((Array.isArray(liste) ? liste : []).find((c) => c.customerNumber === s.kundeid) || null);
        }
      } catch {
        if (!avbrutt) setIkkeFunnet(true);
      }
    })();
    fetch(`${API_BASE_URL}/employees`).then((r) => (r.ok ? r.json() : [])).then((a) => { if (!avbrutt) setAnsatte(a); }).catch(() => {});
    getArbeidsliste('tekstiltrykk').then((l) => { if (!avbrutt) setArbeidsliste(l || []); }).catch(() => {});
    return () => { avbrutt = true; };
  }, [id]);

  useEffect(() => {
    if (!melding) return undefined;
    const t = setTimeout(() => setMelding(''), 5000);
    return () => clearTimeout(t);
  }, [melding]);

  if (ikkeFunnet) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 pb-10">
        <Tilbake til="/service">Tilbake til trykklisten</Tilbake>
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">Fant ikke trykket.</p>
      </div>
    );
  }
  if (!trykk || !form) return <p role="status" className="mx-auto max-w-7xl py-10 text-slate-700">Henter trykket …</p>;

  const lagret = fraTrykk(trykk);
  const harEndringer = JSON.stringify(lagret) !== JSON.stringify(form);
  const lagredeLinjer = linjerForTrykk(trykk);
  const linjerEndret = JSON.stringify(linjer.map(linjeNokkel)) !== JSON.stringify(lagredeLinjer.map(linjeNokkel));
  const kundeNavn = kunde ? [kunde.firstName, kunde.lastName].filter(Boolean).join(' ') : '';
  // Trykk fra en klubbestilling i klubbinfo: klubben og kontaktpersonen der, ikke en kunde
  // (src/lib/klubbKontakt.js). «mottaker» brukes til SMS, etikett og arbeidsseddel.
  const kontaktKlubb = trykk?.kontaktKlubb || null;
  const mottaker = kunde || kontaktSomMottaker(kontaktKlubb);
  const settFelt = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const patch = async (body) => {
    const r = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error('Kunne ikke lagre');
    const s = await r.json();
    setTrykk((t) => ({ ...t, ...s }));
    return s;
  };

  const lagre = async (e) => {
    e?.preventDefault();
    if (!form.beskrivelse.trim()) { setFeil('Skriv hva som skal trykkes.'); return false; }
    setLagrer(true);
    setFeil('');
    try {
      const s = await patch({
        Varemerke: form.Varemerke,
        Produkt: form.Produkt,
        Størrelse: form.Størrelse,
        Farge: form.Farge,
        Beskrivelse: form.beskrivelse,
        utførtArbeid: form.utførtArbeid,
        ansatt: form.ansatt,
        status: form.status,
        toyBetalt: form.toyBetalt,
        trykkBetalt: form.trykkBetalt,
        ferdigDato: form.ferdigDato ? `${form.ferdigDato}T${form.ferdigTid || '00:00'}:00.000Z` : null,
      });
      setForm(fraTrykk(s));
      setMelding(s.ordreAvsluttet ? `Trykket er lagret, og ordre ${s.ordreAvsluttet} er avsluttet.` : 'Trykket er lagret.');
      return true;
    } catch {
      setFeil('Klarte ikke å lagre. Prøv igjen.');
      return false;
    } finally {
      setLagrer(false);
    }
  };

  // Arbeid og produkter lagres med en gang de legges til, endres eller fjernes (TrykkLinjer).
  const lagreLinjer = async (nye = linjer) => {
    setLagrerLinjer(true);
    setLinjeFeil('');
    setLinjeMelding('');
    try {
      const s = await patch({ prislinjer: nye });
      setLinjer(linjerForTrykk(s));
      setLinjeMelding(s.tilbudOppdatert ? 'Lagret, og pristilbudet er oppdatert.'
        : s.tilbudUtdatert ? 'Lagret. Pristilbudet er allerede sendt og endres ikke; send det på nytt, så får kunden en ny versjon med ny lenke.'
        : 'Lagret.');
      return s;
    } catch {
      setLinjeFeil('Klarte ikke å lagre. Prøv igjen.');
      return null;
    } finally {
      setLagrerLinjer(false);
    }
  };

  // «Tøyet er betalt» og «Trykket er betalt» lagres med en gang.
  const lagreBetaling = async (felt, verdi) => {
    setLagrerLinjer(true);
    setLinjeFeil('');
    setLinjeMelding('');
    setForm((f) => ({ ...f, [felt]: verdi }));
    try {
      await patch({ [felt]: verdi });
      setLinjeMelding('Betalingen er lagret.');
    } catch {
      setForm((f) => ({ ...f, [felt]: !verdi }));
      setLinjeFeil('Klarte ikke å lagre betalingen. Prøv igjen.');
    } finally {
      setLagrerLinjer(false);
    }
  };

  const skrivUtEtikett = async () => {
    if (harEndringer && !(await lagre())) return;
    if (!mottaker) return;
    const w = window.open('', '', 'width=500,height=300');
    const d = new Date();
    const dato = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    w.document.write(`<html><head><style>
      @page { size: 90mm 29mm; margin: 0; }
      @media print { body { width: 90mm; height: 29mm; margin: 0; padding: 0; overflow: hidden; display: flex;
        flex-direction: column; justify-content: center; align-items: center; font-family: Arial, sans-serif; } }
      .navn { font-size: 10px; text-align: center; } .nr { font-size: 20px; text-align: center; font-weight: bold; }
      </style></head><body>
      <div class="navn">${kontaktKlubb ? `${escapeHtml(kontaktKlubb.klubbNavn)}: ` : ''}${escapeHtml(mottaker.firstName)} ${escapeHtml(mottaker.lastName)} (${dato})</div>
      <div class="nr">Trykknummer: ${escapeHtml(String(trykk.serviceid ?? ''))}</div>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
    w.close();
  };

  const skrivUtArbeidsseddel = async () => {
    if (harEndringer && !(await lagre())) return;
    let aktuell = trykk;
    if (linjerEndret) {
      const s = await lagreLinjer();
      if (!s) return;
      aktuell = { ...trykk, ...s };
    }
    const vindu = window.open('', '', 'width=900,height=1100');
    if (!vindu) {
      setFeil('Nettleseren stoppet utskriftsvinduet. Tillat sprettoppvinduer for denne siden og prøv igjen.');
      return;
    }
    vindu.document.write(arbeidsseddelHtml({ trykk: aktuell, kunde: mottaker, felt: form, linjer: linjerForTrykk(aktuell) }));
    vindu.document.close();
    vindu.focus();
    setTimeout(() => vindu.print(), 300);
  };

  const registrert = trykk.registrertDato ? formatDateTime(trykk.registrertDato) : 'ukjent dato';

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      {kunde ? <Tilbake til={`/customer-details/${kunde._id}`}>Tilbake til kundekortet</Tilbake>
        : kontaktKlubb ? <Tilbake til={`/klubber/${kontaktKlubb.klubbId}`}>Tilbake til klubben</Tilbake>
        : <Tilbake til="/service">Tilbake til trykklisten</Tilbake>}

      <Sidetopp
        ikon="genser"
        farge="trykk"
        tittel={`${kontaktKlubb ? 'Klubbtrykk' : 'Trykk'} ${trykk.serviceid ?? ''}`}
        undertittel={[trykk.Varemerke, trykk.Produkt].filter(Boolean).join(' ') || 'Navn eller logo på klær'}
      >
        {kontaktKlubb && <KlubbMerke klubbNavn={kontaktKlubb.klubbNavn} tekst="Klubbtrykk" />}
      </Sidetopp>

      {/* Kunden (lenke til kundekortet) */}
      {kunde && (
        <KundeBanner
          kundeId={kunde._id}
          navn={kundeNavn}
          telefon={kunde.phoneNumber}
          epost={kunde.email}
          linje={<>Registrert {registrert}{trykk.ansatt ? ` av ${trykk.ansatt}` : ''}</>}
        />
      )}

      {/* Laget fra en ordre med «Overfør til trykk»: avsluttes sammen med ordren (src/lib/ordreTrykk.js). */}
      {trykk.fraOrdre?.id && (
        <p className="flex flex-wrap items-start gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-1.5 text-violet-950">
          <Ikon navn="kvittering" className="mt-0.5 w-5 h-5 shrink-0" />
          <span className="min-w-0 flex-1">
            Fra{' '}
            <Link to={`/order-details/${trykk.fraOrdre.id}`} className="font-semibold text-indigo-700 underline underline-offset-2">
              ordre {trykk.fraOrdre.ordreid}
            </Link>{' '}
            {formatDateTime(trykk.fraOrdre.dato)}{trykk.fraOrdre.av ? ` av ${trykk.fraOrdre.av}` : ''}. Trykket og ordren avsluttes sammen når varene er levert.
          </span>
        </p>
      )}

      {kontaktKlubb && (
        <KlubbKontaktBanner kontakt={kontaktKlubb} linje={<>Registrert {registrert}{trykk.ansatt ? ` av ${trykk.ansatt}` : ''}</>} />
      )}

      <SmsTilKunden telefon={mottaker?.phoneNumber} />

      <div className="grid gap-2 sm:grid-cols-3">
        <button type="button" onClick={skrivUtEtikett} disabled={!mottaker} className={knappSekundar}>
          <Ikon navn="lapp" className="w-5 h-5" /> Skriv ut etikett (trykknr. {trykk.serviceid})
        </button>
        <button type="button" onClick={skrivUtArbeidsseddel} className={knappSekundar}>
          <Ikon navn="dokument" className="w-5 h-5" /> Skriv ut arbeidsseddel
        </button>
        <button type="button" onClick={() => navigate('/sendsms', { state: { serviceDetails: trykk, customer: mottaker } })} className={knappSekundar}>
          <Ikon navn="send" className="w-5 h-5" /> Send SMS
        </button>
      </div>

      {/* Trykket */}
      <form onSubmit={lagre} className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-3">
        <h2 className="text-base font-bold text-slate-900">Trykket</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Tekstfelt id="tr-merke" etikett="Varemerke" name="Varemerke" value={form.Varemerke} onChange={settFelt} />
          <Tekstfelt id="tr-produkt" etikett="Produkt" name="Produkt" value={form.Produkt} onChange={settFelt} />
          <Tekstfelt id="tr-str" etikett="Størrelse/lengde" name="Størrelse" value={form.Størrelse} onChange={settFelt} />
          <Tekstfelt id="tr-farge" etikett="Farge" name="Farge" value={form.Farge} onChange={settFelt} />
        </div>
        <Tekstfelt
          id="tr-beskrivelse"
          etikett="Hva skal trykkes?"
          hjelp="Navn, nummer, logo og plassering. Internt: kunden ser ikke dette."
          flerlinjet
          name="beskrivelse"
          value={form.beskrivelse}
          onChange={settFelt}
          required
        />
        <Tekstfelt id="tr-utfort" etikett="Utført arbeid (internt)" flerlinjet name="utførtArbeid" value={form.utførtArbeid} onChange={settFelt} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Valgfelt id="tr-ansatt" etikett="Ansatt" name="ansatt" value={form.ansatt} onChange={settFelt}>
            <option value="">Velg en ansatt</option>
            {form.ansatt && !ansatte.some((a) => a.navn === form.ansatt) && <option value={form.ansatt}>{form.ansatt}</option>}
            {ansatte.map((a) => <option key={a._id} value={a.navn}>{a.navn}</option>)}
          </Valgfelt>
          <div className="min-w-0 space-y-1">
            <Valgknapper id="tr-status" etikett="Status" valg={STATUSER} verdi={form.status} onVelg={(v) => setForm((f) => ({ ...f, status: v }))} />
            {trykk.fraOrdre?.id && form.status === 'Avsluttet' && lagret.status !== 'Avsluttet' && (
              <p role="status" className="text-sm font-semibold text-violet-900">Ordre {trykk.fraOrdre.ordreid} avsluttes også når du lagrer.</p>
            )}
          </div>
        </div>
        <fieldset className="min-w-0 space-y-2">
          <legend className="font-semibold text-slate-800">Lovet ferdig</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <label htmlFor="tr-dato" className="mb-1 block font-medium text-slate-800">Dato</label>
              <input id="tr-dato" type="date" name="ferdigDato" value={form.ferdigDato} onChange={settFelt} className={feltKlasse} />
            </div>
            <div className="min-w-0">
              <label htmlFor="tr-tid" className="mb-1 block font-medium text-slate-800">Klokkeslett</label>
              <input id="tr-tid" type="time" name="ferdigTid" value={form.ferdigTid} onChange={settFelt} className={feltKlasse} />
            </div>
          </div>
        </fieldset>
        {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}
        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" disabled={lagrer || !harEndringer} className={knappPrimar}>
            <Ikon navn="hake" className="w-6 h-6" /> {lagrer ? 'Lagrer …' : 'Lagre endringer'}
          </button>
          <p role="status" className="text-sm text-slate-700">{melding || (harEndringer ? 'Endringene er ikke lagret.' : '')}</p>
        </div>
      </form>

      {/* Arbeid og produkter: prislinjene (grunnlaget for arbeidsseddelen og pristilbudet) */}
      <TrykkLinjer
        linjer={linjer}
        arbeidsliste={arbeidsliste}
        onLagre={async (nye) => !!(await lagreLinjer(nye))}
        lagrer={lagrerLinjer}
        betaling={{ toyBetalt: form.toyBetalt, trykkBetalt: form.trykkBetalt }}
        onBetaling={lagreBetaling}
        melding={linjeMelding}
        feil={linjeFeil}
      />

      {/* Pristilbudet går til kunden, eller kontaktpersonen i klubben (mottaker). */}
      <TrykkTilbud
        trykk={trykk}
        kunde={mottaker}
        klubb={kontaktKlubb}
        linjer={linjer}
        ulagret={linjerEndret}
        onLagreLinjer={() => lagreLinjer(linjer)}
        onTilbud={(tilbud) => setTrykk((t) => ({ ...t, tilbud }))}
      />

      {/* Kundelenken bygger på kundekortet, så den er ikke på klubbestillinger. */}
      {!kontaktKlubb && (
      <OrdreMeldinger
        api="services"
        ordre={trykk}
        kunde={kunde}
        smsLenke={smsLenke}
        onOppdatert={(s) => setTrykk((t) => ({ ...t, ...s }))}
      />
      )}

    </div>
  );
}

export default ServiceDetailsClub;
